



import { useApi } from './api';
import type { ClaimRecord, PaginatedResponse, ClaimCreateDTO, ApiResponse } from '../types';

export const useClaimService = () => {
  const { apiFetch } = useApi();

  return {
    getClaimsPaginated: async (page: number, size: number, status?: string, referralId?: number): Promise<PaginatedResponse<ClaimRecord>> => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });
      
      if (status && status.trim()) {
        params.append('status', status.trim());
      }
      
      if (referralId) {
        params.append('referralId', referralId.toString());
      }
      
      try {
        const response = await apiFetch(`/claims?${params}`);
        
        console.log('Claims response status:', response.status);
        console.log('Claims response headers:', response.headers.get('content-type'));
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response body:', errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const responseText = await response.text();
          console.error('Non-JSON response:', responseText);
          throw new Error('El servidor no devolvió JSON válido');
        }
        
        const data = await response.json();
        console.log('Claims data received:', data);
        return data;
        
      } catch (error) {
        console.error('Error in getClaimsPaginated:', error);
        throw error;
      }
    },

    getClaimById: async (id: number): Promise<ClaimRecord> => {
      const response = await apiFetch(`/claims/${id}`);
      
      if (!response.ok) {
        throw new Error(`Error fetching claim: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    },

    getClaimsByReferralId: async (referralId: number): Promise<ClaimRecord[]> => {
      const response = await apiFetch(`/claims/referral/${referralId}`);
      
      if (!response.ok) {
        throw new Error(`Error fetching claims for referral: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    },

    createClaim: async (claim: ClaimCreateDTO, referralId: number): Promise<ApiResponse<ClaimRecord>> => {
      const params = new URLSearchParams({
        referralId: referralId.toString(),
      });

      const response = await apiFetch(`/claims?${params}`, {
        method: 'POST',
        body: JSON.stringify(claim),
        headers: { 'Content-Type': 'application/json' },
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Error al crear el claim',
        };
      }
      
      return {
        success: true,
        data: data.data,
        message: data.message,
      };
    },

    updateClaim: async (id: number, claim: ClaimCreateDTO): Promise<ApiResponse<ClaimRecord>> => {
      const response = await apiFetch(`/claims/${id}`, {
        method: 'PUT',
        body: JSON.stringify(claim),
        headers: { 'Content-Type': 'application/json' },
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Error al actualizar el claim',
        };
      }
      
      return {
        success: true,
        data: data.data,
        message: data.message,
      };
    },

    markClaimsReadyForSync: async (claimIds: number[]): Promise<ApiResponse<ClaimRecord[]>> => {
      const response = await apiFetch('/claims/mark-ready-for-sync', {
        method: 'POST',
        body: JSON.stringify({ claimIds }),
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Error al marcar citas como listas para sincronizar',
        };
      }

      return {
        success: true,
        data: data.data,
        message: data.message,
      };
    },

    deleteClaims: async (ids: number[]): Promise<ApiResponse<void>> => {
      const params = new URLSearchParams();
      ids.forEach(id => params.append('ids', id.toString()));

      const response = await apiFetch(`/claims?${params}`, { 
        method: 'DELETE' 
      });
      
      if (!response.ok) {
        const data = await response.json();
        return {
          success: false,
          error: data.error || 'Error al eliminar los claims',
        };
      }
      
      const data = await response.json();
      return {
        success: true,
        message: data.message || 'Claims eliminados exitosamente',
      };
    },

    // ========================================
    // ENDPOINTS DE SINCRONIZACIÓN
    // ========================================

    getPendingSyncClaims: async (): Promise<ClaimRecord[]> => {
      const response = await apiFetch('/claims/pending-sync');
      
      if (!response.ok) {
        throw new Error('Error fetching pending sync claims');
      }
      
      return await response.json();
    },

    updateSyncStatus: async (id: number, success: boolean, errorMessage?: string): Promise<ApiResponse<ClaimRecord>> => {
      const params = new URLSearchParams({
        success: success.toString(),
      });
      
      if (errorMessage) {
        params.append('errorMessage', errorMessage);
      }

      const response = await apiFetch(`/claims/${id}/sync-status?${params}`, {
        method: 'PUT',
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Error al actualizar estado de sincronización',
        };
      }
      
      return {
        success: true,
        data: data.data,
        message: data.message,
      };
    },

    // ========================================
    // ENDPOINTS DE ESTADÍSTICAS/DASHBOARD
    // ========================================

    getTotalClaims: async (): Promise<number> => {
      const response = await apiFetch('/claims/count');
      
      if (!response.ok) {
        throw new Error('Error fetching claims count');
      }
      
      return await response.json();
    },

    getClaimsByStatusCount: async (status: string): Promise<number> => {
      const response = await apiFetch(`/claims/count/status/${status}`);
      
      if (!response.ok) {
        throw new Error(`Error fetching claims count for status ${status}`);
      }
      
      return await response.json();
    },

    getDashboardStats: async (): Promise<Record<string, number>> => {
      const response = await apiFetch('/claims/dashboard/stats');
      
      if (!response.ok) {
        throw new Error('Error fetching claim dashboard stats');
      }
      
      return await response.json();
    }
  };
};